import type { ReactNode } from 'react'
import { core } from '@musinsa/pbo-core'
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import { PboAlert } from './pboDialog'

/**
 * 2.0 화면 안내 기준 — 무엇을 토스트로, 무엇을 알럿으로 띄우는지 한 곳에서 정한다(사용자 결정 2026-10-02, 기준 A).
 *
 *  · 검증(요청 전 화면이 막는 것 — 필수·선택 없음·조건·개수·형식·권한·순서) → 토스트(`invalid`)
 *    단, 특정 행·건을 짚는 문구(“3번째 행의 주소가 비어 있습니다”, “주문번호 X 에 …”)는 놓치면 못 찾으니 알럿(`rowInvalid`)
 *  · 실패(서버 거절·처리 실패·부분 실패·잘림·진행 중 대기) → 새 디자인 알럿(`failure`)
 *  · 성공 → 토스트(`success`) · 되묻는 확인 → `PboConfirm`(pboDialog)
 *
 *  · 예외 — 스캔 화면(반품작업·반품작업_신규): 스캐너 앞 작업자가 확인 버튼에 막히지 않게 **실패도 토스트**, 대신 아주 크게(`scanNotice`).
 *    PD 2026-10-01 "알럿이 뜨면 다음 스캔이 막힌다" + 사용자 2026-10-02 "스캔 실패 토스트 좋은데 너무 작다, 엄청 크게".
 *
 * 옛 mwms-ds 알럿(`MAlert`·`MConfirm`)과 브라우저 `window.confirm` 은 2.0 화면에서 쓰지 않는다.
 * 확인창 슬롯(`*_ok`)의 검증 실패는 안내 뒤 **throw** 한다 — `false` 는 "성공인데 토스트 생략"이라 생성본이 선택을 비운다(가이드 52 규칙 10).
 */
export const notice = {
  /** 검증 — 토스트(경고). */
  invalid: (message: string): void => {
    core.message.warning(message)
  },
  /** 특정 행·건을 짚는 검증 — 알럿(확인을 눌러야 닫힌다). */
  rowInvalid: (message: ReactNode): void => {
    PboAlert.show(message)
  },
  /** 실패·부분 실패·잘림·대기 — 알럿. */
  failure: (message: ReactNode): void => {
    PboAlert.show(message)
  },
  /** 성공 — 토스트. */
  success: (message: string): void => {
    core.message.success(message)
  },
}

/** 안내하고 던질 오류를 돌려준다 — 확인창 슬롯에서 `throw invalidError(…)` 로 확인창·선택을 남긴다. */
export const invalidError = (message: string): Error => {
  notice.invalid(message)
  return new Error(message)
}
export const rowInvalidError = (message: string): Error => {
  notice.rowInvalid(message)
  return new Error(message)
}
export const failureError = (message: string): Error => {
  notice.failure(message)
  return new Error(message)
}

/**
 * 지금 화면에 2.0(pbo-core) 화면이 보이는가 — 전역 실패 처리(queryClient·엑셀 다운로드)가 옵션 없이 불린 경우에도
 * 2.0 화면이면 새 디자인 알럿, 옛 화면이면 옛 알럿을 고르는 기준. 2.0 화면 루트는 모두 `.pbo-core-screen` 을 단다
 * (생성본 셸·PboScope). 탭으로 가려진(보이지 않는) 화면은 세지 않는다.
 */
export const isPboScreenActive = (): boolean =>
  typeof document !== 'undefined' &&
  [...document.querySelectorAll<HTMLElement>('.pbo-core-screen')].some((el) => el.offsetParent !== null || el.getClientRects().length > 0)

/**
 * 스캔 화면 전용 큰 토스트 — 작업자가 화면에서 한 걸음 떨어져 있어도 읽히게 글자 24 · 아이콘 32, 화면 위쪽 1/4 지점, 기본보다 오래(4초) 둔다.
 * 확인을 누르지 않아도 사라지므로 다음 스캔을 막지 않는다. **한 번에 한 장** — 새 안내가 오면 앞 토스트를 바꾼다(스캔은 마지막 결과가 중요하고,
 *  여러 장이 쌓이면 장마다 위 여백이 붙어 화면에 흩어진다).
 */
const SCAN_TOAST_DURATION_SEC = 4
const SCAN_TOAST_KEY = 'pbo-scan-toast'
/** 아이콘 색 = antd 기본 팔레트(colorWarning·colorError·colorSuccess) — 기본 토스트 아이콘과 같은 색이라 크기만 다르고 뜻은 같아 보인다. */
const SCAN_TOAST_TONE = {
  warning: { icon: 'ic-alertCircle', color: '#faad14' }, // antd colorWarning
  error: { icon: 'ic-closeCircle', color: '#ff4d4f' }, // antd colorError
  success: { icon: 'ic-checkCircle', color: '#52c41a' }, // antd colorSuccess
} as const
const SCAN_TOAST_ICON_SIZE = 32
const SCAN_TOAST_ICON_STYLE = { display: 'inline-flex', lineHeight: 0, marginInlineEnd: 12, verticalAlign: 'middle' } as const
/** 글자 24(기본 토스트 14) · 줄바꿈 유지 · 긴 문구는 화면 폭 80% 에서 접는다. */
const SCAN_TOAST_TEXT_STYLE = {
  display: 'inline-block',
  verticalAlign: 'middle',
  fontSize: 24,
  lineHeight: 1.4,
  fontWeight: 600,
  whiteSpace: 'pre-line',
  maxWidth: 'min(720px, 80vw)',
  textAlign: 'left',
  padding: '6px 4px',
} as const
/** 화면 위쪽 1/4 지점 — 맨 위(기본 토스트 자리)보다 작업자 시선 높이에 가깝게. */
const SCAN_TOAST_POSITION_STYLE = { marginTop: '22vh' } as const

const scanToast = (type: keyof typeof SCAN_TOAST_TONE, message: string): void => {
  const tone = SCAN_TOAST_TONE[type]
  core.message.open({
    key: SCAN_TOAST_KEY,
    type,
    duration: SCAN_TOAST_DURATION_SEC,
    icon: (
      <span style={SCAN_TOAST_ICON_STYLE}>
        <DsIcon name={tone.icon} size={SCAN_TOAST_ICON_SIZE} color={tone.color} filled />
      </span>
    ),
    content: (
      <span style={SCAN_TOAST_TEXT_STYLE}>
        {message}
      </span>
    ),
    style: SCAN_TOAST_POSITION_STYLE,
  })
}

export const scanNotice = {
  /** 검증 — 큰 토스트(경고). */
  invalid: (message: string): void => scanToast('warning', message),
  /** 특정 행·건을 짚는 검증 — 스캔 화면은 알럿 대신 큰 토스트(경고). 확인 버튼이 없어 다음 스캔을 막지 않는다(invalid 와 같은 동작, 호출부 의도 표시용). */
  rowInvalid: (message: string): void => scanToast('warning', message),
  /** 실패·부분 실패·대기 — 큰 토스트(오류). */
  failure: (message: string): void => scanToast('error', message),
  /** 성공 — 큰 토스트. */
  success: (message: string): void => scanToast('success', message),
}
export const scanInvalidError = (message: string): Error => {
  scanNotice.invalid(message)
  return new Error(message)
}
export const scanRowInvalidError = (message: string): Error => {
  scanNotice.rowInvalid(message)
  return new Error(message)
}
export const scanFailureError = (message: string): Error => {
  scanNotice.failure(message)
  return new Error(message)
}
